import { Stack } from "@mui/material";
import useIsCreate from "hooks/useIsCreate";
import BackPageButton from "components/buttons/BackPageButton";
import ROUTES from "constants/routes";
import { Competency_Map_Tabs_Values, GRADE_OPTIONS, QUESTION_TYPES, QUESTIONS_TABS_VALUES } from "constants/global";
import { useParams, useSearchParams } from "react-router-dom";
import qs from "qs";
import { useMemo, useState } from "react";
import {
  Language,
  Question_Type,
  useCompetencyMapHeaderQuery,
  useQuestionQuery,
  useSkillBasicQuery,
} from "@generated/graphql";
import { useForm } from "react-hook-form";
import Form from "components/form/Form";
import { escapeHtmlCharsInCodeBlock, replaceNLSybmolWithBr } from "helpers/CKEditor";
import { formatNumber } from "helpers/index";
import CircularLoading from "components/CircularLoading";
import ViewGridLayout from "layout/ViewGridLayout";
import Header from "./components/Header";
import Info from "./components/Info";
import { QuestionInputs } from "./types";
import LeftColumn from "./components/LeftColumn";

const QuestionView = () => {
  const isCreate = useIsCreate();
  const [searchParams] = useSearchParams();
  const { id } = useParams();
  const params = useMemo(() => qs.parse(searchParams.toString()), [searchParams]) as {
    skillId?: string;
    mapId?: string;
  };
  const { skillId: skillIdParam, mapId } = params;
  const [isEditing, setIsEditing] = useState<boolean>(true);

  const { data: questionData, loading: questionLoading } = useQuestionQuery({
    variables: {
      id: id!,
    },
    fetchPolicy: "cache-and-network",
  });
  const question = questionData?.question;
  const skillId = question?.skill.id || skillIdParam;

  const { data: skillData } = useSkillBasicQuery({
    variables: {
      skillId: skillId!,
    },
  });
  const skill = skillData?.skill;

  const { data: competencyMapData } = useCompetencyMapHeaderQuery({
    variables: { competencyMapId: mapId! },
    skip: !mapId,
  });
  const competencyMap = competencyMapData?.competencyMap;

  const getRecommendedTime = () => {
    if (question?.recommendedTime) {
      const recommendedTimeToMinutes = Math.ceil(question.recommendedTime / 60);
      return formatNumber(["минута", "минуты", "минут"], recommendedTimeToMinutes);
    }
    return undefined;
  };

  const form = useForm<QuestionInputs>({
    values: {
      knowledgeBlock: {
        title: skill?.knowledgeBlock.title || "",
      },
      title: skill?.title || "",
      time: String(question ? question.time / 60 : 1),
      criterion: question?.criterion?.map((value) => ({ value })) || [],
      recommendedTime: getRecommendedTime() || "",
      text: replaceNLSybmolWithBr(escapeHtmlCharsInCodeBlock(question?.text || "")),
      grade: question ? GRADE_OPTIONS.find((grade) => grade.id === question?.grade) || null : null,
      type: question ? QUESTION_TYPES.find((type) => type.id === question?.type) || null : null,
      subtype: question?.subtype || "",
      language: question?.language || Language.Ru,
      partsInput:
        question?.parts?.map(({ id: partId, content, isCorrect }) => ({ id: partId, content, isCorrect })) || [],
      referenceAnswer: question?.referenceAnswer || "",
    },
  });

  const { watch } = form;
  const questionType = watch("type");
  const isMultiChoiceQuestion = questionType?.id === Question_Type.MultipleChoiceQuestions;

  const getQuestionsTabValue = (action: "submit" | "cancel") => {
    if (action === "submit") return QUESTIONS_TABS_VALUES.unconfirmed;

    if (isCreate || question?.confirmation) return QUESTIONS_TABS_VALUES.confirmed;

    return QUESTIONS_TABS_VALUES.unconfirmed;
  };

  const getMapBackLocation = () => ({
    pathname: `/${ROUTES.COMPETENCE_MAPS}/view/${mapId}`,
    search: qs.stringify({
      tab: Competency_Map_Tabs_Values.questionsList,
      questionId: question?.id,
    }),
  });

  const getSkillBackLocation = (action: "submit" | "cancel") => ({
    pathname: `/${ROUTES.SKILL}/view/${skillId}`,
    search: qs.stringify({
      tab: getQuestionsTabValue(action),
    }),
  });

  const getBackPageButtonProps = (action: "submit" | "cancel") => {
    return mapId
      ? {
          text: `Карта компетенций «${competencyMap?.title}»`,
          link: getMapBackLocation(),
        }
      : {
          text: `Навык «${skill?.title}»`,
          link: getSkillBackLocation(action),
        };
  };

  if (questionLoading) return <CircularLoading />;

  return (
    <>
      <BackPageButton {...getBackPageButtonProps("cancel")} />
      <Form form={form}>
        <Stack
          spacing={2.5}
          useFlexGap>
          <Header />
          <ViewGridLayout
            containerSx={{ flexWrap: "nowrap", flexDirection: { md: "row", xs: "column-reverse" } }}
            LeftColumn={
              <LeftColumn
                isEditing={isEditing}
                skillId={skillId}
                isMultiChoiceQuestion={isMultiChoiceQuestion}
              />
            }
            RightColumn={
              <Info
                question={question}
                skill={skill}
                isEditing={isEditing}
                setIsEditing={setIsEditing}
              />
            }
          />
        </Stack>
      </Form>
    </>
  );
};

export default QuestionView;
